import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {environment} from 'src/environments/environment';
import {AuthService} from '../services/auth/auth.service';
import {ServiceTokenService} from '../services/auth/service-token.service';
import {CSRF_HEADER, csrfToken} from '../utils/csrf-token';
import {AUTH_CLIENT, AUTH_CLIENT_HEADER, isAiApiUrl} from '../utils/auth-client';

/**
 * Central auth transport for the console:
 *  - Requests to the gatherco V3 API and the Node V1 API (environment.nodeApiUrl) authenticate via
 *    the HttpOnly auth cookie (withCredentials) plus the double-submit CSRF header. No Authorization
 *    header. NOTE: cookie auth on the Node API requires the cookie to reach that host — true locally
 *    (shared localhost) but in staging/prod the Node API is a different host, so the auth cookie must
 *    be issued with a shared parent domain there.
 *  - Requests to the AI API authenticate with the service token as a Bearer header, plus the
 *    X-Auth-Client identity header the AI API requires alongside it (without it a valid token
 *    still comes back 401).
 */
@Injectable()
export class CredentialsInterceptor implements HttpInterceptor {

  constructor(private serviceTokenService: ServiceTokenService, private authService: AuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isGathercoApi = req.url.startsWith(environment.apiUrl);
    const isNodeApi = req.url.startsWith(environment.nodeApiUrl);
    const isAiApi = isAiApiUrl(req.url, environment.aiApiUrl);

    if (isGathercoApi || isNodeApi) {
      // The session has just been torn down and we are on our way to /login. Anything a
      // still-mounted page fires between now and the redirect is guaranteed to come back 401, so
      // fail it here instead of putting another doomed authenticated request on the wire. Rejecting
      // upstream of ErrorInterceptor also means these do not produce a second round of toasts.
      if (this.authService.isSessionEnding()) {
        return throwError(() => new HttpErrorResponse({status: 401, statusText: 'Unauthorized', url: req.url}));
      }

      // X-Auth-Client scopes the server to the console's own (backend) cookie so it never falls back
      // to the frontend cookie when both apps are logged in on the shared API host.
      const headers: Record<string, string> = {[AUTH_CLIENT_HEADER]: AUTH_CLIENT};

      // The CSRF token rides EVERY request to these APIs, not just the state-changing ones. The
      // server only enforces it on POST/PUT/PATCH/DELETE, so sending it on a GET costs nothing (the
      // request is already non-simple because of X-Auth-Client, so no extra CORS pre-flight), and it
      // removes a whole class of "the write went out without a token" bug: one rule, every method,
      // no per-call opt-in to forget. See utils/csrf-token.ts for where the token comes from.
      const token = csrfToken();
      if (token) {
        headers[CSRF_HEADER] = token;
      }

      // No response-header capture. That fallback claimed to read the token back out of a
      // `X-XSRF-TOKEN` RESPONSE header, but no API has ever sent one and it is in no
      // `Access-Control-Expose-Headers` list (v3 exposes only the four `X-Pagination-*`), so the
      // captured value was always null — dead code. Since the cookie is now scoped per client and
      // per environment, it was also the one remaining path by which this console could present
      // another app's token, which is exactly what the rename set out to stop.
      return next.handle(req.clone({withCredentials: true, setHeaders: headers}));
    }

    // Cross-host AI API: authenticate with the service token as a Bearer header, fetching it on
    // demand if it isn't loaded yet so calls right after login / on first dashboard load don't race.
    // X-Auth-Client rides along on every AI request — the AI API answers a request without it with
    // 401 even when the token is valid — and is set here rather than per call site so anything
    // going through HttpClient (BaseAiService and friends) gets it for free. The two SSE clients
    // that must use `fetch` set it themselves; interceptors never see those.
    return this.serviceTokenService.ensureToken().pipe(
      switchMap((token) => this.sendWithToken(req, next, token, isAiApi)),
      catchError((err) => {
        // The service token is minted once (bootstrap / after login) and held in memory for the life
        // of the tab with no expiry we can inspect, so the AI API answering 401 IS the first sign it
        // has aged out. That says nothing about the console's own session — the cookie the rest of
        // the app authenticates with is routinely still valid — so re-mint the token and replay the
        // request rather than letting the failure reach the user.
        //
        // Exactly one retry: renew() has already replaced the cached token, so a 401 on the replay is
        // a real refusal and propagates (ErrorInterceptor reports it WITHOUT ending the session).
        if (!isAiApi || err?.status !== 401) {
          return throwError(() => err);
        }
        return this.serviceTokenService.renew().pipe(
          switchMap((token) => token
            ? this.sendWithToken(req, next, token, isAiApi)
            : throwError(() => err))
        );
      })
    );
  }

  /** Send `req` to the cross-host API with the bearer token and, on the AI host, the client identity. */
  private sendWithToken(
    req: HttpRequest<any>,
    next: HttpHandler,
    token: string | null,
    isAiApi: boolean
  ): Observable<HttpEvent<any>> {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (isAiApi) {
      headers[AUTH_CLIENT_HEADER] = AUTH_CLIENT;
    }
    return next.handle(Object.keys(headers).length ? req.clone({setHeaders: headers}) : req);
  }
}
